import { refreshBenefitQueries } from '../sponsorship/benefitQueries';
import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import PageLayout from '../../components/layout/PageLayout';
import baseline from '../../app-ui/baseline';
import copy from './visibilityCopy.json';
import './ProfileVisibilityPage.css';

const unwrap = (response) => response?.data?.data ?? response?.data;
const token = () => localStorage.getItem('POSTPOP_AUTH_TOKEN');

export default function ProfileVisibilityPage() {
  const user = useUserStore((s) => s.user);
  const loading = useUserStore((s) => s.loading);
  const [session, setSession] = useState(0);
  const sessionToken = useRef(token());
  useEffect(
    () =>
      useUserStore.subscribe((next, previous) => {
        if (
          next.user?.id !== previous.user?.id ||
          next.isLogin !== previous.isLogin ||
          next.isAdmin !== previous.isAdmin ||
          token() !== sessionToken.current
        ) {
          sessionToken.current = token();
          setSession((n) => n + 1);
        }
      }),
    [],
  );
  if (!user)
    return loading ? null : <Navigate replace to="/login?redirectUrl=%2Fmypage%2Fvisibility" />;
  return (
    <VisibilityPreferences
      key={`${user.id}:${session}`}
      user={user}
      renewSession={() => {
        sessionToken.current = token();
        setSession((n) => n + 1);
      }}
    />
  );
}

function VisibilityPreferences({ user, renewSession }) {
  const { locale } = useWebLocale();
  const { resolvedMode } = useColorScheme();
  const t = copy[locale === 'en' ? 'en' : 'ko'];
  const colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const navigate = useNavigate();
  const client = useQueryClient();
  const [values, setValues] = useState({
    showCommentsOnProfile: user.showCommentsOnProfile ?? true,
    showSavedOnProfile: user.showSavedOnProfile ?? true,
  });
  const [support, setSupport] = useState(null);
  const [supportLoading, setSupportLoading] = useState(true);
  const [supportError, setSupportError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState({ profile: false, roster: false });
  const [error, setError] = useState('');
  const scope = useRef({ alive: true, revision: 0, requests: new Set(), locks: new Set() });
  const sessionToken = useRef(token());

  useEffect(() => {
    const s = scope.current;
    s.alive = true;
    const cancel = () => {
      s.revision += 1;
      s.requests.forEach((controller) => controller.abort());
    };
    const unsubscribe = useUserStore.subscribe((next, previous) => {
      if (
        next.user?.id !== previous.user?.id ||
        next.isLogin !== previous.isLogin ||
        next.isAdmin !== previous.isAdmin ||
        token() !== sessionToken.current
      )
        cancel();
    });
    return () => {
      s.alive = false;
      cancel();
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    setValues({
      showCommentsOnProfile: user.showCommentsOnProfile ?? true,
      showSavedOnProfile: user.showSavedOnProfile ?? true,
    });
  }, [user.showCommentsOnProfile, user.showSavedOnProfile]);

  const requestScope = () => {
    const s = scope.current;
    const revision = s.revision;
    const capturedToken = token();
    const controller = new AbortController();
    s.requests.add(controller);
    const sameIdentity = () =>
      s.alive &&
      s.revision === revision &&
      !controller.signal.aborted &&
      useUserStore.getState().user?.id === user.id;
    const current = () => sameIdentity() && token() === capturedToken;
    return {
      current,
      controller,
      options: { signal: controller.signal, suppressGlobalError: true, isSessionCurrent: current },
      finish: () => {
        s.requests.delete(controller);
        // A refreshed credential must not leave stale optimistic controls locked.
        if (sameIdentity() && token() !== capturedToken) {
          s.revision += 1;
          s.requests.forEach((pending) => pending.abort());
          renewSession();
        }
      },
    };
  };

  useEffect(() => {
    const request = requestScope();
    setSupportLoading(true);
    setSupportError(false);
    api
      .getAuth('/sponsorships/me', request.options)
      .then((response) => {
        if (request.current()) setSupport(unwrap(response));
      })
      .catch(() => {
        if (request.current()) setSupportError(true);
      })
      .finally(() => {
        if (request.current()) setSupportLoading(false);
        request.finish();
      });
    return () => request.controller.abort();
    // The component is keyed by account/session. Preference updates do not reload eligibility.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const invalidate = () => {
    void refreshBenefitQueries(client);
  };

  const save = async (key, value) => {
    if (token() !== sessionToken.current) {
      renewSession();
      return;
    }
    const roster = key === 'supporterRosterVisible';
    const kind = roster ? 'roster' : 'profile';
    if (scope.current.locks.has(kind) || useUserStore.getState().user?.id !== user.id) return;
    if (roster && (!support || !(support.isSupporter || support.adminBenefitsAccess))) return;
    const request = requestScope();
    const previous = roster ? support : values[key];
    const payload = roster
      ? { ...support.preferences, supporterRosterVisible: value }
      : { [key]: value };
    scope.current.locks.add(kind);
    setBusy((s) => ({ ...s, [kind]: true }));
    setError('');
    if (roster) setSupport({ ...support, preferences: payload });
    else setValues((s) => ({ ...s, [key]: value }));
    try {
      const response = await api.putAuth(
        roster ? '/sponsorships/me/preferences' : '/me',
        payload,
        request.options,
      );
      if (!request.current()) return;
      if (roster) {
        const updated = unwrap(response);
        setSupport(updated?.preferences ? updated : { ...previous, preferences: payload });
      } else {
        useUserStore.setState((state) => ({ user: { ...state.user, [key]: value } }));
      }
      invalidate();
    } catch (_) {
      if (!request.current()) return;
      if (roster) setSupport(previous);
      else setValues((s) => ({ ...s, [key]: previous }));
      setError(roster ? 'sponsor.preferenceFailed' : 'profileVisibility.saveFailedTitle');
    } finally {
      scope.current.locks.delete(kind);
      if (request.current()) setBusy((s) => ({ ...s, [kind]: false }));
      request.finish();
    }
  };
  const row = (key, label, description, value, disabled) => (
    <div className="pp-visibility-row" key={key}>
      <div className="pp-visibility-copy">
        <span id={`${key}-label`}>{t[label]}</span>
        <p id={`${key}-description`}>{t[description]}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-labelledby={`${key}-label`}
        aria-describedby={`${key}-description`}
        disabled={disabled}
        className="pp-visibility-switch"
        onClick={() => save(key, !value)}
      >
        <span />
      </button>
    </div>
  );
  return (
    <PageLayout paddingTop={0}>
      <section
        className="pp-visibility"
        style={{
          '--visibility-text': colors.text,
          '--visibility-secondary': colors.textSecondary,
          '--visibility-border': colors.divider,
          '--visibility-track': colors.dividerStrong,
        }}
      >
        <header>
          <button
            type="button"
            aria-label={t['common.back']}
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/mypage'))}
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{t['profileVisibility.title']}</h1>
        </header>
        <p className="pp-visibility-intro">{t['profileVisibility.description']}</p>
        <div className="pp-visibility-group">
          <Link className="pp-visibility-row" to={`/users/${encodeURIComponent(user.nickname)}`}>
            <div className="pp-visibility-copy">
              <span>{t['settings.publicProfile']}</span>
              <p>{t['settings.publicProfileDescription']}</p>
            </div>
            <FiChevronRight size={17} strokeWidth={1.8} aria-hidden="true" />
          </Link>
          {row(
            'showCommentsOnProfile',
            'profileEdit.showCommentsOnProfile',
            'profileEdit.showCommentsOnProfileBody',
            values.showCommentsOnProfile,
            busy.profile,
          )}
          {row(
            'showSavedOnProfile',
            'profileVisibility.showSavedOnProfile',
            'profileVisibility.showSavedOnProfileBody',
            values.showSavedOnProfile,
            busy.profile,
          )}
          {(support?.isSupporter || support?.adminBenefitsAccess) &&
            row(
              'supporterRosterVisible',
              'sponsor.rosterSetting',
              'sponsor.rosterSettingBody',
              support.preferences?.supporterRosterVisible ?? support.adminBenefitsAccess ?? false,
              busy.roster,
            )}
        </div>
        {supportLoading && <p role="status">{t['common.loading']}</p>}
        {supportError && (
          <div role="alert">
            <p>
              {locale === 'en'
                ? 'Could not load supporter settings.'
                : '후원자 설정을 불러오지 못했어요.'}
            </p>
            <button type="button" onClick={() => setAttempt((n) => n + 1)}>
              {t['common.retry']}
            </button>
          </div>
        )}
        {error && (
          <div role="alert">
            <strong>{t[error]}</strong>
            <p>{t['profileVisibility.saveFailedBody']}</p>
          </div>
        )}
      </section>
    </PageLayout>
  );
}
